import _ from 'lodash';
import { FEATURE_FLAGS_NAMES, PROJECT_STATUS } from '@linearb/linjs-common';
import styled from 'styled-components';
import { Link, Tooltip } from '@material-ui/core';
import AppRouter from 'common/AppRouter';
import { Flex } from 'components/base/layout-components';
import Icon from 'components/base/Icon';
import Dropdown from 'components/base/Dropdown';
import Text, { Inline } from 'components/base/Text';
import {
  getProjectsPeriodsOptions,
  PROJECTS,
  updatedAt
} from '../services/projects.services';
import MenuButton from '../components/MenuButton';
import LoadingProjectData from '../components/LoadingProjectData';
import ProjectLabelsAndCadence from '../components/ProjectLabelsAndCadence';
import { iterationTitle } from '../services/projectExtended.services';
import {
  CUSTOM_RANGE_MIN_DATE,
  SUMMARY_PERIODS,
  SUMMARY_PERIODS_DEFAULT
} from '../projects.consts';
import { useHistory } from 'react-router';
import RangePicker from 'components/base/RangePicker';
import { momentDateValidator } from 'common/utils/time.utils';
import moment from 'moment';
import { SEARCH_DATE_FORMAT } from 'common/constants/constants';
import useSettingsService from 'components/hooks/useSettingsService';
import useLastUpdatedValue from '../hooks/useLastUpdatedValue';
import { LBButton } from '@linearb/buzz';

const EditDeleteMenuButtonWrapper = styled(Flex)`
  justify-content: flex-end;
  padding-right: 2rem;
`;

const getCurrentIterationIndex = ({
  iterations,
  selectedIteration,
  selectedPeriod
}) =>
  selectedPeriod === SUMMARY_PERIODS.last_sprint
    ? iterations.length - 1
    : iterations.findIndex(
        item => _.get(item, 'id') === _.get(selectedIteration, 'id')
      );

const getPreviousIteration = ({
  selectedPeriod,
  iterations,
  selectedIteration
}) => {
  const currentIndex = getCurrentIterationIndex({
    iterations,
    selectedPeriod,
    selectedIteration
  });
  if (currentIndex - 1 >= 0) {
    return iterations[currentIndex - 1];
  }
  return null;
};
const getNextIteration = ({
  selectedPeriod,
  iterations = [],
  selectedIteration = {}
}) => {
  const currentIndex = getCurrentIterationIndex({
    iterations,
    selectedPeriod,
    selectedIteration
  });
  if (currentIndex + 1 < iterations.length) {
    return iterations[currentIndex + 1];
  }
  return null;
};
const ProjectTopBar = ({
  project,
  lastIteration,
  toggleModal,
  toggleSnackBar,
  pmSensorAuthId,
  isCanConfigProjects,
  selectedIteration,
  selectedPeriod,
  setSelectedPeriod,
  setLastSelectedPeriod,
  setSelectedIteration,
  isSingleIterationView,
  lastSelectedPeriod,
  iterations,
  onProjectEdited,
  isFavorite,
  selectedTimeRange,
  setSelectedTimeRange
}) => {
  const { getSettingOrDefinitionValue } = useSettingsService();
  const history = useHistory();

  const lastUpdated = useLastUpdatedValue({ projectId: project?.id });

  const pdtDateRangePickerEnabled = getSettingOrDefinitionValue({
    name: FEATURE_FLAGS_NAMES.pdtDateRangePickerEnable
  });
  const { name, status } = project;
  const isLoading = status === PROJECT_STATUS.EDITED;
  const previousIteration = getPreviousIteration({
    selectedPeriod,
    iterations,
    selectedIteration
  });
  const nextIteration = getNextIteration({
    selectedPeriod,
    iterations,
    selectedIteration
  });

  const goToProjects = ({ deletedProjectId } = {}) => {
    const paramsObj = {
      duration_filter:
        selectedPeriod === SUMMARY_PERIODS.single_iteration
          ? SUMMARY_PERIODS.last_sprint
          : selectedPeriod,
      ...(deletedProjectId ? { deleted_project_id: deletedProjectId } : {}),
      ...selectedTimeRange
    };

    history.push({
      pathname: isFavorite
        ? AppRouter.PROJECTS_API_FAVORITES.path
        : AppRouter.PROJECTS_API_ALL.path,
      search: new URLSearchParams(paramsObj).toString()
    });
  };

  return (
    <>
      <Flex grow='1' items='center'>
        <Flex items='center'>
          <Icon name='Projects' size='medium' color='shark' isThin='true' />
          <Text size='large' pl='1rem'>
            <Link
              id='backToProjects'
              onClick={() => {
                goToProjects();
              }}
            >
              Projects
            </Link>
          </Text>
          <Inline pl='1rem'> / </Inline>
          {isSingleIterationView ? (
            <Text size='large' pl='1rem' data-testid='project-name'>
              <Link
                id='backToProject'
                onClick={() => {
                  setSelectedPeriod(
                    lastSelectedPeriod || SUMMARY_PERIODS_DEFAULT
                  );
                  setSelectedIteration(null);
                }}
              >
                {name}
              </Link>
            </Text>
          ) : (
            <Tooltip
              placement='bottom-start'
              title={
                <ProjectLabelsAndCadence
                  project={project}
                  isExtendedView={true}
                />
              }
            >
              <Text size='large' pl='1rem' data-testid='project-name'>
                {name}
              </Text>
            </Tooltip>
          )}
        </Flex>
        <Flex
          data-testid='project-selectedPeriod-wrapper'
          pl='small'
          grow='1'
          items='center'
          gap='0.5rem'
          justify={isSingleIterationView && 'center'}
        >
          {isSingleIterationView && (
            <Tooltip
              placement='top-end'
              title={
                <Flex direction='column'>
                  <Text size='medium' pb='0.5rem'>
                    Previous iteration:
                  </Text>
                  {iterationTitle(previousIteration, true)}
                </Flex>
              }
            >
              <LBButton
                variant='outline'
                size='icon'
                disabled={!previousIteration}
                onClick={() => {
                  setSelectedPeriod(SUMMARY_PERIODS.single_iteration);
                  setSelectedIteration(previousIteration);
                }}
              >
                <Icon name='chevronleft' size='small' />
              </LBButton>
            </Tooltip>
          )}

          <div data-testid='project-selectedPeriod'>
            <Dropdown
              width='auto'
              options={getProjectsPeriodsOptions(
                true,
                lastIteration,
                selectedIteration
              ).filter(
                ({ value }) =>
                  pdtDateRangePickerEnabled || value !== SUMMARY_PERIODS.custom
              )}
              selectedValue={selectedPeriod}
              onChange={selected => {
                if (
                  ![
                    SUMMARY_PERIODS.single_iteration,
                    SUMMARY_PERIODS.last_sprint
                  ].includes(selected)
                ) {
                  setLastSelectedPeriod(selected);
                  setSelectedIteration(null);
                } else if (selected === SUMMARY_PERIODS.last_sprint) {
                  setSelectedIteration(iterations.at(-1));
                }
                setSelectedPeriod(selected);
              }}
            />
          </div>

          {pdtDateRangePickerEnabled &&
            selectedPeriod === SUMMARY_PERIODS.custom && (
              <Flex width='18rem' pr='small' pl='small' height='2.7rem'>
                <RangePicker
                  minDate={CUSTOM_RANGE_MIN_DATE}
                  height='1.2rem'
                  isOutsideRange={day => day.isAfter(moment(), 'day')}
                  startDate={momentDateValidator(
                    _.get(selectedTimeRange, 'start_date')
                  )}
                  endDate={momentDateValidator(
                    _.get(selectedTimeRange, 'end_date')
                  )}
                  onDatesChange={({ endDate, startDate }) => {
                    const newTimeframe = {
                      end_date: moment(endDate).format(SEARCH_DATE_FORMAT),
                      start_date: moment(startDate).format(SEARCH_DATE_FORMAT)
                    };
                    setSelectedTimeRange(newTimeframe);
                  }}
                />
              </Flex>
            )}
          {isSingleIterationView ? (
            <Tooltip
              placement='top-start'
              title={
                <Flex direction='column'>
                  <Text size='medium' pb='0.5rem'>
                    Next iteration:
                  </Text>
                  {iterationTitle(nextIteration, true)}
                </Flex>
              }
            >
              <LBButton
                variant='secondary'
                size='icon'
                disabled={!nextIteration}
                onClick={() => {
                  if (nextIteration.id === lastIteration.id) {
                    setSelectedPeriod(SUMMARY_PERIODS.last_sprint);
                  } else {
                    setSelectedPeriod(SUMMARY_PERIODS.single_iteration);
                  }

                  setSelectedIteration(nextIteration);
                }}
              >
                <Icon name='chevronright' size='small' />
              </LBButton>
            </Tooltip>
          ) : (
            <Text
              pl='1rem'
              color={PROJECTS.SECONDARY_COLOR}
              data-testid='project-updated'
            >
              {isLoading ? (
                <LoadingProjectData projectState={{}} isExtendedView={true} />
              ) : (
                `Updated: ${updatedAt(lastUpdated)}`
              )}
            </Text>
          )}
        </Flex>
      </Flex>

      {isCanConfigProjects && (
        <EditDeleteMenuButtonWrapper borderBox>
          <MenuButton
            project={project}
            pmSensorAuthId={pmSensorAuthId}
            toggleModal={toggleModal}
            toggleSnackBar={toggleSnackBar}
            deleteCallback={() => {
              goToProjects({ deletedProjectId: project.id });
            }}
            onSaveClick={onProjectEdited}
          />
        </EditDeleteMenuButtonWrapper>
      )}
    </>
  );
};

export default ProjectTopBar;
